import { supabase } from "./supabase";

// ─── SUPABASE DATA HELPERS ────────────────────────────────────────────────────
// All data operations go through Supabase.
// localStorage is kept as a fast local cache / offline fallback.

function lsGet(key) { try { var v=localStorage.getItem(key); return v?JSON.parse(v):null; } catch(e){ return null; } }
function lsSet(key,val) { try { localStorage.setItem(key,JSON.stringify(val)); } catch(e){} }

// ─── EQUIPMENT ────────────────────────────────────────────────────────────────

export async function fetchEquipment(companyId) {
  var cached = lsGet("ch_equipment_"+companyId);
  if (cached) return cached; // return cache immediately
  var res = await supabase.from("equipment").select("*").eq("company_id", companyId).order("created_at");
  if (res.error) return [];
  var items = res.data.map(dbToEquipment);
  lsSet("ch_equipment_"+companyId, items);
  return items;
}

export async function saveEquipment(companyId, eq) {
  var row = equipmentToDb(companyId, eq);
  var res = await supabase.from("equipment").upsert(row, { onConflict: "id" });
  if (!res.error) invalidateCache("ch_equipment_"+companyId);
  return !res.error;
}

export async function deleteEquipmentById(companyId, eqId) {
  var res = await supabase.from("equipment").delete().eq("id", eqId).eq("company_id", companyId);
  if (!res.error) invalidateCache("ch_equipment_"+companyId);
  return !res.error;
}

// ─── LOGS ─────────────────────────────────────────────────────────────────────

export async function fetchLogs(companyId) {
  var cached = lsGet("ch_logs_"+companyId);
  if (cached) return cached;
  var res = await supabase.from("logs").select("*").eq("company_id", companyId).order("date", { ascending: false });
  if (res.error) return [];
  var items = res.data.map(dbToLog);
  lsSet("ch_logs_"+companyId, items);
  return items;
}

export async function saveLog(companyId, log) {
  var row = logToDb(companyId, log);
  var res = await supabase.from("logs").upsert(row, { onConflict: "id" });
  if (!res.error) invalidateCache("ch_logs_"+companyId);
  return !res.error;
}

// ─── REPAIRS ──────────────────────────────────────────────────────────────────

export async function fetchRepairs(companyId) {
  var cached = lsGet("ch_repairs_"+companyId);
  if (cached) return cached;
  var res = await supabase.from("repairs").select("*").eq("company_id", companyId).order("date", { ascending: false });
  if (res.error) return [];
  var items = res.data.map(dbToRepair);
  lsSet("ch_repairs_"+companyId, items);
  return items;
}

export async function saveRepair(companyId, repair) {
  var row = repairToDb(companyId, repair);
  var res = await supabase.from("repairs").upsert(row, { onConflict: "id" });
  if (!res.error) invalidateCache("ch_repairs_"+companyId);
  return !res.error;
}

// ─── REQUESTS ─────────────────────────────────────────────────────────────────

export async function fetchRequests(companyId) {
  var cached = lsGet("ch_requests_"+companyId);
  if (cached) return cached;
  var res = await supabase.from("requests").select("*").eq("company_id", companyId).order("created_at", { ascending: false });
  if (res.error) return [];
  var items = res.data.map(dbToRequest);
  lsSet("ch_requests_"+companyId, items);
  return items;
}

export async function saveRequest(companyId, req) {
  var row = requestToDb(companyId, req);
  var res = await supabase.from("requests").upsert(row, { onConflict: "id" });
  if (!res.error) invalidateCache("ch_requests_"+companyId);
  return !res.error;
}

export async function updateRequest(companyId, req) {
  return saveRequest(companyId, req);
}

// ─── CACHE ────────────────────────────────────────────────────────────────────

function invalidateCache(key) {
  try { localStorage.removeItem(key); } catch(e){}
}

export function invalidateAll(companyId) {
  invalidateCache("ch_equipment_"+companyId);
  invalidateCache("ch_logs_"+companyId);
  invalidateCache("ch_repairs_"+companyId);
  invalidateCache("ch_requests_"+companyId);
}

// ─── DATA MAPPERS ─────────────────────────────────────────────────────────────

function dbToEquipment(row) {
  return {
    id: row.id,
    name: row.name,
    type: row.type,
    subtype: row.subtype,
    make: row.make,
    model: row.model,
    year: row.year,
    usesMiles: row.uses_miles,
    totalUsage: row.total_usage,
    status: row.status,
    outOfService: row.out_of_service,
    vin: row.vin,
    serialNumber: row.serial_number,
    engineOilType: row.engine_oil_type,
    engineOilQty: row.engine_oil_qty,
    oilFilterPN: row.oil_filter_pn,
    airFilterPN: row.air_filter_pn,
    fuelFilterPN: row.fuel_filter_pn,
    sparkPlug: row.spark_plug,
    tireSize: row.tire_size,
    hydraulicOilType: row.hydraulic_oil_type,
    hydraulicOilQty: row.hydraulic_oil_qty,
    lastServiceDate: row.last_service_date,
    lastServiceUsage: row.last_service_usage,
    lastServiceNotes: row.last_service_notes,
    maintenanceIntervals: row.maintenance_intervals || [],
    customParts: row.custom_parts || [],
    warranty: row.warranty,
  };
}

function equipmentToDb(companyId, eq) {
  return {
    id: eq.id,
    company_id: companyId,
    name: eq.name,
    type: eq.type,
    subtype: eq.subtype || "",
    make: eq.make || "",
    model: eq.model || "",
    year: eq.year || "",
    uses_miles: eq.usesMiles || false,
    total_usage: eq.totalUsage || 0,
    status: eq.status || "active",
    out_of_service: eq.outOfService || null,
    vin: eq.vin || "",
    serial_number: eq.serialNumber || "",
    engine_oil_type: eq.engineOilType || "",
    engine_oil_qty: eq.engineOilQty || "",
    oil_filter_pn: eq.oilFilterPN || "",
    air_filter_pn: eq.airFilterPN || "",
    fuel_filter_pn: eq.fuelFilterPN || "",
    spark_plug: eq.sparkPlug || "",
    tire_size: eq.tireSize || "",
    hydraulic_oil_type: eq.hydraulicOilType || "",
    hydraulic_oil_qty: eq.hydraulicOilQty || "",
    last_service_date: eq.lastServiceDate || "",
    last_service_usage: eq.lastServiceUsage || 0,
    last_service_notes: eq.lastServiceNotes || "",
    maintenance_intervals: eq.maintenanceIntervals || [],
    custom_parts: eq.customParts || [],
    warranty: eq.warranty || null,
  };
}

function dbToLog(row) {
  return {
    id: row.id,
    equipmentId: row.equipment_id,
    equipmentName: row.equipment_name,
    date: row.date,
    usage: row.usage,
    driver: row.driver,
    driverId: row.driver_id,
    fuelGallons: row.fuel_gallons,
    fuelCost: row.fuel_cost,
    notes: row.notes,
    status: row.status,
  };
}

function logToDb(companyId, log) {
  return {
    id: log.id,
    company_id: companyId,
    equipment_id: log.equipmentId,
    equipment_name: log.equipmentName || "",
    date: log.date,
    usage: log.usage,
    driver: log.driver || null,
    driver_id: log.driverId || null,
    fuel_gallons: log.fuelGallons || null,
    fuel_cost: log.fuelCost || null,
    notes: log.notes || null,
    status: log.status || "green",
  };
}

function dbToRepair(row) {
  return {
    id: row.id,
    equipmentId: row.equipment_id,
    date: row.date,
    whatFixed: row.what_fixed,
    repairType: row.repair_type,
    cost: row.cost,
    laborHours: row.labor_hours,
    doneAtHours: row.done_at_hours,
    technician: row.technician,
    notes: row.notes,
    photos: row.photos || [],
  };
}

function repairToDb(companyId, repair) {
  return {
    id: repair.id,
    company_id: companyId,
    equipment_id: repair.equipmentId,
    date: repair.date,
    what_fixed: repair.whatFixed || "",
    repair_type: repair.repairType || "",
    cost: repair.cost || null,
    labor_hours: repair.laborHours || null,
    done_at_hours: repair.doneAtHours || null,
    technician: repair.technician || null,
    notes: repair.notes || null,
    photos: repair.photos || [],
  };
}

function dbToRequest(row) {
  return {
    id: row.id,
    equipmentId: row.equipment_id,
    equipmentName: row.equipment_name,
    requestedBy: row.requested_by,
    requestedById: row.requested_by_id,
    title: row.title,
    details: row.details,
    priority: row.priority,
    status: row.status,
    autoGenerated: row.auto_generated,
    adminNotes: row.admin_notes,
    resolvedAt: row.resolved_at,
    resolvedBy: row.resolved_by,
    createdAt: row.created_at,
  };
}

function requestToDb(companyId, req) {
  return {
    id: req.id,
    company_id: companyId,
    equipment_id: req.equipmentId || null,
    equipment_name: req.equipmentName || "",
    requested_by: req.requestedBy || "",
    requested_by_id: req.requestedById || null,
    title: req.title || "",
    details: req.details || "",
    priority: req.priority || "soon",
    status: req.status || "pending",
    auto_generated: req.autoGenerated || false,
    admin_notes: req.adminNotes || "",
    resolved_at: req.resolvedAt || null,
    resolved_by: req.resolvedBy || null,
  };
}
